import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { ScalrBreadcrumb } from '@/components/customComponents'
import FilterBar from '@/components/FilterBar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { FEATURE_MATRIX } from '@/config/features'
import { toast, useKpiGoals } from '@/hooks'
import {
  addAlert,
  selectTenant,
  useAppDispatch,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'

import LaborPage from '@/screens/clarity/LaborPage'
import LoyaltyPage from '@/screens/clarity/LoyaltyPage'
import ProfitAndLossPage from '@/screens/clarity/PnlPage'
import SalesPage from '@/screens/clarity/SalesPage'
import SpeedOfServicePage from '@/screens/clarity/SpeedOfServicePage'
import SpendPage from '@/screens/clarity/SpendPage'
import { NotAllowedClarityPage } from '@/screens/error'

import { KpiGoalsModel } from '@/types'
import { dateToString } from '@/utils'
import { addSampleAlerts } from '@/utils/alertDemo'

function PageContent({ pageId }: { pageId: string }) {
  const { t } = useTranslation()
  const tenant = useAppSelector(selectTenant)
  // Use dynamic pageId to get correct date context (monthly vs quarterly)
  // This ensures KPI goals receive the correct dates for quarterly pages (spend, profit-and-loss)
  const { selectedBranch, date } = useReduxBaseFilter(pageId || 'sales')
  const features = FEATURE_MATRIX[tenant]

  if (!pageId) {
    return null
  }

  const {
    kpiGoals,
    loading: kpiGoalsLoading,
    error: kpiGoalsError,
  } = useKpiGoals(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    pageId,
    selectedBranch,
    tenant
  )

  // Show error toast if there is an error
  useEffect(() => {
    if (kpiGoalsError) {
      toast({
        title: t('common.errors.serverError'),
        description: kpiGoalsError.message,
        variant: 'destructive',
      })
    }
  }, [kpiGoalsError])

  return (
    <>
      <FilterBar pageId={pageId} />

      {pageId === 'sales' &&
        (features.claritySales ? (
          <SalesPage
            kpiGoals={kpiGoals as KpiGoalsModel}
            kpiGoalsLoading={kpiGoalsLoading}
          />
        ) : (
          <NotAllowedClarityPage pageTitle={t('clarity.navigation.sales')} />
        ))}

      {pageId === 'labor' &&
        (features.clarityLabor ? (
          <LaborPage
            kpiGoals={kpiGoals as KpiGoalsModel}
            kpiGoalsLoading={kpiGoalsLoading}
          />
        ) : (
          <NotAllowedClarityPage pageTitle={t('clarity.navigation.labor')} />
        ))}

      {pageId === 'spend' &&
        (features.claritySpend ? (
          <SpendPage
            kpiGoals={kpiGoals as KpiGoalsModel}
            kpiGoalsLoading={kpiGoalsLoading}
          />
        ) : (
          <NotAllowedClarityPage pageTitle={t('clarity.navigation.spend')} />
        ))}

      {pageId === 'speed-of-service' &&
        (features.claritySpeedOfService ? (
          <SpeedOfServicePage
            kpiGoals={kpiGoals as KpiGoalsModel}
            kpiGoalsLoading={kpiGoalsLoading}
          />
        ) : (
          <NotAllowedClarityPage
            pageTitle={t('clarity.navigation.speedOfService')}
          />
        ))}

      {pageId === 'loyalty' &&
        (features.clarityLoyalty ? (
          <LoyaltyPage
            kpiGoals={kpiGoals as KpiGoalsModel}
            kpiGoalsLoading={kpiGoalsLoading}
          />
        ) : (
          <NotAllowedClarityPage pageTitle={t('clarity.navigation.loyalty')} />
        ))}

      {pageId === 'profit-and-loss' &&
        (features.clarityProfitAndLoss ? (
          <ProfitAndLossPage />
        ) : (
          <NotAllowedClarityPage
            pageTitle={t('clarity.navigation.profitAndLoss')}
          />
        ))}
    </>
  )
}

export default function CockpitPage() {
  const { t } = useTranslation()
  const { id } = useParams()

  const dispatch = useAppDispatch()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!hasInitialized.current) {
      addSampleAlerts({
        addAlert: (alert: Parameters<typeof addAlert>[0]) =>
          dispatch(addAlert(alert)),
      } as any)
      hasInitialized.current = true
    }
  }, [dispatch])

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle pageTitle>
          {id === 'sales' && t('clarity.navigation.sales')}
          {id === 'labor' && t('clarity.navigation.labor')}
          {id === 'spend' && t('clarity.navigation.spend')}
          {id === 'speed-of-service' && t('clarity.navigation.speedOfService')}
          {id === 'loyalty' && t('clarity.navigation.loyalty')}
          {id === 'profit-and-loss' && t('clarity.navigation.profitAndLoss')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <ScalrBreadcrumb
          breadcrumbItems={[
            { label: t('clarity.navigation.clarity'), href: '/clarity/sales' },
          ]}
          title={
            id === 'sales'
              ? t('clarity.navigation.sales')
              : id === 'labor'
                ? t('clarity.navigation.labor')
                : id === 'spend'
                  ? t('clarity.navigation.spend')
                  : id === 'speed-of-service'
                    ? t('clarity.navigation.speedOfService')
                    : id === 'loyalty'
                      ? t('clarity.navigation.loyalty')
                      : t('clarity.navigation.profitAndLoss')
          }
        />

        <PageContent pageId={id as string} />
      </CardContent>
    </Card>
  )
}
